export type CheckoutPaymentMethod = "pix" | "credit" | "pix_installment" | "pix_deferred" | "direct_cnpj";

const PAYMENT_METHOD_ALIASES: Record<string, "pix" | "credit_card"> = {
  pix: "pix",
  credit_card: "credit_card",
  credit: "credit_card",
  cartao: "credit_card",
  "cartão": "credit_card",
};

export const normalizeSupplierPaymentMethods = (
  value: string | string[] | null | undefined,
): Array<"pix" | "credit_card"> => {
  const rawMethods = Array.isArray(value) ? value : String(value ?? "").split(",");

  return Array.from(new Set(
    rawMethods
      .map((method) => PAYMENT_METHOD_ALIASES[method.trim().toLowerCase()])
      .filter((method): method is "pix" | "credit_card" => Boolean(method)),
  ));
};

export const isCheckoutPaymentMethodAllowed = (
  method: CheckoutPaymentMethod | null,
  allowedMethods: string[],
  pixInstallmentEnabled: boolean,
  deferredPixAllowed: boolean = false,
  directPaymentAllowed: boolean = false,
): boolean => {
  if (!method) return true;
  // Pagamento via CNPJ (fora da plataforma): liberado pelo fornecedor por proposta; não depende do Pix/cartão da loja.
  if (method === "direct_cnpj") return directPaymentAllowed;

  const normalized = normalizeSupplierPaymentMethods(allowedMethods);
  if (method === "credit") return normalized.includes("credit_card");
  if (method === "pix_installment") {
    return pixInstallmentEnabled && normalized.includes("pix");
  }
  if (method === "pix_deferred") {
    return deferredPixAllowed && normalized.includes("pix");
  }
  return normalized.includes("pix");
};
/**
 * Pagamento pós-evento: só em propostas (mode=orcamento), com a chave geral de
 * Finanças ligada e pelo menos um prazo habilitado. Não depende do cardápio.
 */
export const shouldOfferDeferredPix = ({
  isQuoteMode,
  deferredPixEnabled,
  daysOptions,
}: {
  isQuoteMode: boolean;
  deferredPixEnabled: boolean;
  daysOptions: number[] | null | undefined;
}): boolean =>
  isQuoteMode === true &&
  deferredPixEnabled === true &&
  Array.isArray(daysOptions) &&
  daysOptions.some((d) => Number.isFinite(d) && d > 0);

/**
 * Confirmação do pagamento pós-evento no checkout: nada é cobrado agora.
 * A reserva fica "programado" com pagamento pendente até o vencimento.
 */
export const buildDeferredPixConfirmation = (nowIso: string) => ({
  payment_method: "pix_deferred" as const,
  status: "programado",
  status_payments: "pending",
  confirmed_at: nowIso,
  updated_at: nowIso,
});

/** Vencimento do pós-evento: data do evento + prazo escolhido na proposta (YYYY-MM-DD). */
export const deferredPixDueDate = (eventDate: string, days: number): string => {
  const [y, m, d] = eventDate.split("T")[0].split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().split("T")[0];
};
